/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { InlineModel, Location } from '@flipp/shopper-ad-web-schemas';
import {
  AnalyticBeacons,
  AnalyticsQueue,
  type AnalyticsTagEventTypes,
  attachClickEvent,
  attachObserver,
  CallbackType,
  convertToISOString,
  createBaseBeacon,
  createCustomContentIframe,
  createCustomContentWrapper,
  createExperienceMeasuredTestBeacon,
  createUlpClickBeacon,
  createUlpEngagementBeacon,
  createUlpErrorBeacon,
  createUlpHeartbeatBeacon,
  createUlpItemImpressionBeacon,
  createUlpOpenBeacon,
  createUlpSeeAllDealsBeacon,
  createUlpViewBeacon,
  DeviceService,
  getBeaconPayload,
  getDecisionParameters,
  getIntegrationType,
  getPlatform,
  getSessionId,
  getSiteDomain,
  getUlpItemType,
  getUserKey,
  injectCustomContent,
  isIntersectionObserverSupported,
  type KevelCustomData,
  type LoaderOptions,
  MeasureTest,
  PostMessageEvent,
  REQUEST_PREFIX,
  Tags,
  buildLiveRampTag,
  translateDecisionToAvro,
  type ULPImpressionMessage,
  type ULPMessage,
  ULPMessageEvent,
  UXType,
  validHexColor,
  getFlyerLegacyIds,
  asNumber,
} from '@flipp/shopper-ad-web-shared';
import { scrollInterval } from './config';
import { createTaboolaFooter } from './footers/taboola';
import { calculateGeomData } from './geom';
import { getHasBeenEngaged, trackHeartbeat } from './heartbeat';
import { isInGoogleAdsIframe, thisIframeInParent } from './iframeHelpers';
import { resizeGamContainer } from './GAMHelpers';
import { throttle } from './throttle';
import { watchAdmixerViewable } from './admixerViewable';

export const SCROLL_INTERVAL = 66;

export const MAX_WIDTH = 1024;
export const MIN_WIDTH = 300;
export const MAX_HEIGHT = 2400;
export const COMPACT_HEIGHT = 600;
export const WIDE_COMPACT_FIXED_HEIGHT = 600;
export const PREVIEW_HEIGHT = 200;
export const MIN_GOOGLE_AMP_HEIGHT = 250;
export const DEFAULT_STEP_HEIGHT = 1500;

export const ULPErrors = [
  "Cannot read properties of undefined (reading 'map')",
  'Failed to fetch',
  "Cannot read properties of undefined (reading 'length')",
  "undefined is not an object (evaluating 'de.ecom_items.map')",
  "undefined is not an object (evaluating 'de.ecom_items.length')",
  'Load failed',
  'No items are rendered in the ULP',
];

type ExperienceConstructorParams = {
  targetEl: Readonly<HTMLElement>;
  publisherNameIdentifier: string;
  data: Readonly<InlineModel>;
  options: Readonly<LoaderOptions>;
  resizeCallback: (exp: Readonly<Experience>, height: number) => null;
  siteId: number;
  zoneIds: readonly number[];
  handlePreviewEnded: (exp: Readonly<Experience>) => null;
  geoData: Readonly<Location>;
  keywords: string[];
  positionIndex: number | null;
};

export class Experience {
  private readonly targetEl: HTMLElement;
  private readonly publisherNameIdentifier: string;
  private readonly data: InlineModel;
  private readonly options: LoaderOptions;
  private readonly keywords: string[];
  private readonly resizeCallback: (
    exp: Readonly<Experience>,
    height: number,
  ) => null;
  private readonly handlePreviewEnded: (exp: Readonly<Experience>) => null;

  private containerWindow?: Window;
  private containerElement?: HTMLElement;
  private _contentElement?: HTMLElement;
  private iframe?: HTMLIFrameElement;

  private kevelMetadata?: KevelCustomData;
  private creativeHeight?: number;
  private creativeWidth?: number;
  private engagementUrl?: string;
  private impressionUrl?: string;
  private liveRampTag?: string | null;

  private readonly siteId: number;
  private readonly zoneIds: readonly number[];
  private readonly geoData: Location;
  private readonly positionIndex: number | null;

  private hasScrolledBeyond = false;

  private readonly intersectionObserver: boolean;

  private _preview = false;
  private _expandCount = 0;
  private _hoverExpandable = false;

  public get preview() {
    return this._preview;
  }

  public get expandCount() {
    return this._expandCount;
  }

  public get contentElement() {
    return this._contentElement;
  }

  public get hoverExpandable() {
    return this._hoverExpandable;
  }

  constructor({
    targetEl,
    publisherNameIdentifier,
    data,
    options,
    resizeCallback,
    siteId,
    zoneIds,
    handlePreviewEnded,
    geoData,
    keywords = [],
    positionIndex,
  }: Readonly<ExperienceConstructorParams>) {
    this.targetEl = targetEl;
    this.publisherNameIdentifier = publisherNameIdentifier;
    this.data = data;
    this.options = options;
    this.keywords = keywords;
    this.resizeCallback = resizeCallback;
    this.handlePreviewEnded = handlePreviewEnded;
    this.siteId = siteId;
    this.zoneIds = zoneIds;
    this._preview = options.preview ?? false;
    this.geoData = geoData;
    this.intersectionObserver = isIntersectionObserverSupported();
    this.positionIndex = positionIndex;
  }

  sendMessage(type: string, msgData: unknown) {
    if (!this.iframe) {
      return null;
    }

    this.iframe.contentWindow?.postMessage(
      {
        type,
        data: msgData,
      },
      '*',
    );
  }

  sendCountdownState(countdownEnd?: number, countdownInterval?: number) {
    this.sendMessage(PostMessageEvent.COUNTDOWN_STATE, {
      countdownEnd,
      countdownInterval,
    });
    return null;
  }

  getDynamicModuleConfig() {
    const dynamicModuleConfig = this.kevelMetadata?.dynamicModule;
    const url = new URL(window.location.toString());
    const templateUrl = url.searchParams.get('flipp-test-template-url');
    if (templateUrl && dynamicModuleConfig) {
      dynamicModuleConfig.templateUrl = templateUrl;
    }
    return dynamicModuleConfig;
  }

  requestExpand() {
    this.sendMessage(PostMessageEvent.REQUEST_EXPAND, true);
    return null;
  }

  private handleSendEv(postMessage: Readonly<{ compositeKey: string }>) {
    if (window.flippxp.evTag[postMessage.compositeKey]) {
      this.sendMessage(PostMessageEvent.SEND_EV, false);
    } else {
      window.flippxp.evTag[postMessage.compositeKey] = true;
      this.sendMessage(PostMessageEvent.SEND_EV, true);
    }
  }

  private composeAppConfig() {
    if (!this.kevelMetadata) {
      return null;
    }

    // Get preferred language
    const locale = navigator.languages
      ? navigator.languages[0]
      : navigator.language;

    const campaignResponseString = JSON.stringify({
      decisions: { inline: [this.data] },
      location: this.geoData,
    });
    const url = new URL(window.location.toString());
    const sessionIdFromUrl = url.searchParams.get('sessionId');
    const itemId = url.searchParams.get('itemId');
    const testStoreCode = url.searchParams.get('flipp-test-store-code');
    const testStoreId = url.searchParams.get('flipp-test-store-id');
    const testMerchantId = url.searchParams.get('flipp-test-merchant-id');
    const testPostalCode = url.searchParams.get('flipp-test-postal-code');
    const startCompact =
      this.options.startCompact === undefined
        ? this.kevelMetadata?.startCompact
        : !!this.options.startCompact;
    const dynamicModule = this.getDynamicModuleConfig();
    const isStandaloneDynamicModule =
      dynamicModule?.standalone ||
      (!this.kevelMetadata.campaignConfigUrl && dynamicModule?.isActive);
    return {
      campaignNameIdentifier: this.kevelMetadata.campaignNameIdentifier,
      ux: this.options.standalone ? UXType.DEFAULT : UXType.INLINE,
      intersectionObserver: this.intersectionObserver,
      siteId: this.siteId,
      site: getSiteDomain(this.options.overrideHostname),
      locale: locale.substring(0, 2),
      country: this.geoData?.country,
      partnerNameIdentifier: this.publisherNameIdentifier,
      campaignConfigUrl: this.kevelMetadata.campaignConfigUrl,
      ulpUrl: this.kevelMetadata.ulpUrl,
      survey: this.kevelMetadata.survey,
      tags: {
        ...this.data?.storefront?.campaignConfig?.tags,
        campaignManagement: {
          open: this.impressionUrl,
          engagement: this.engagementUrl,
          impression: this.liveRampTag,
        },
      },
      expandOnReadMore:
        !!this.options.dwellExpandable || !!this.options.expandOnReadMore,
      preventItemModalClose: this.kevelMetadata.preventItemModalClose,
      creativeEvTimerMs: this.kevelMetadata.evTimerMs,
      hideHeader: this.kevelMetadata.hideHeader,
      themeColorBg: this.kevelMetadata.themeColorBg,
      themeColorFg: this.kevelMetadata.themeColorFg,
      sessionId: sessionIdFromUrl ?? getSessionId(),
      uid: getUserKey(this.options),
      startCompact: this.options.fixedHeightUnit ? false : startCompact,
      dmaPixels: !!this.options.dmaPixels,
      decision: translateDecisionToAvro({
        decision: this.data,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        positionIndex: this.positionIndex,
        customUrl: this.getCustomUrl(),
      }),
      integrationType: getIntegrationType(this.options),
      platform: getPlatform(),
      userPostalCode: testPostalCode ?? this.geoData?.postal_code,
      latitude: this.geoData?.latitude,
      longitude: this.geoData?.longitude,
      regionCode: this.geoData?.region,
      regionName: this.geoData?.region_name,
      accuracyRadius: this.geoData?.accuracy_radius,
      city: this.geoData?.city,
      ip: this.geoData?.ip,
      metroCode: this.geoData?.metro_code,
      storefrontConfig: this.data?.storefront,
      noPopOnLink: !!this.kevelMetadata.noPopOnLink,
      showLocation: !!this.kevelMetadata.showLocation,
      dynamicModule: this.getDynamicModuleConfig(),
      isStandaloneDynamicModule: isStandaloneDynamicModule,
      dynamicPopupIframeUrl: this.kevelMetadata.dynamicPopupIframeUrl,
      dynamicPopupIframeHeight: this.kevelMetadata.dynamicPopupIframeHeight,
      highlightedFlyerItemIds: this.kevelMetadata.highlightedFlyerItemIds ?? [],
      highlightedFlyerItemsOptions:
        this.kevelMetadata.highlightedFlyerItemsOptions,
      targetedOverlayImageUrl:
        this.kevelMetadata.targetedOverlayImageUrl ?? null,
      fallbackImageUrl: this.data?.storefront?.campaignConfig?.fallbackImageUrl,
      fallbackLinkUrl: this.data?.storefront?.campaignConfig?.fallbackLinkUrl,
      dwellTimerMs: this.kevelMetadata.dwellTimerMs,
      hoverExpandable: this._hoverExpandable,
      isVimpCallbackSet: !!window.flippxp.vimpTrackerFunc,
      stepHeight:
        this.kevelMetadata.stepHeight ??
        this.options.stepHeight ??
        DEFAULT_STEP_HEIGHT,
      campaigns: campaignResponseString,
      itemId,
      keywords: this.keywords.filter(e => typeof e === 'string'),
      nearestStores: this.data.nearestStores,
      hideTTMButton: this.kevelMetadata.hideTTMButton,
      hideShareButton: this.kevelMetadata.hideShareButton,
      debugMode: window.flippxp.debugMode,
      hostedUrl: this.kevelMetadata.hostedUrl ?? null,
      mediaPartnerDescriptiveValueTag:
        this.options.mediaPartnerDescriptiveValueTag,
      wideCompact: this.options.wideCompact,
      wideCompactBackgroundColor: this.options.wideCompactBackgroundColor,
      enableOptimizedShimmer: this.options.enableOptimizedShimmer,
      campaignIntlFmt: this.kevelMetadata.campaignIntlFmt,
      customContentHtml: this.kevelMetadata.customContentHtml,
      customContentS3: this.kevelMetadata.customContentS3,
      customContentSection: this.kevelMetadata.customContentSection,
      customContentHeight: this.kevelMetadata.customContentHeight,
      customContentUrl: this.kevelMetadata.customContentUrl,
      customContentSize: this.kevelMetadata.customContentSize,
      storeCode: testStoreCode ?? this.kevelMetadata?.storeCode,
      storeId: testStoreId ?? this.kevelMetadata?.storeId,
      merchantId: testMerchantId
        ? asNumber(testMerchantId)
        : this.kevelMetadata?.merchantId,
      fixedHeightUnit: this.options.fixedHeightUnit,
      fixedHeightUnitItemModal: this.options.fixedHeightUnitItemModal,
      publicationGlobalId:
        this.options.publicationGlobalId || this.data.publication?.global_id,
      publicationRenderMetadataTypes:
        this.options.publicationRenderMetadataTypes ||
        this.data.publication?.rendering_metadata_types,
      fixedHeightDynamicModule: this.options.fixedHeightDynamicModule,
      enableV2Schema:
        this.kevelMetadata.enableV2Schema || this.options.enableV2Schema,
      resizeWideInserts: this.options.resizeWideInserts,
      disableFallbackImage: this.options.disableFallbackImage,
    };
  }

  private handleInlineGeometry() {
    if (
      !this.containerElement ||
      !this.containerWindow ||
      !this.contentElement
    ) {
      return null;
    }

    const geom = calculateGeomData(
      this.containerElement,
      this.contentElement,
      this.containerWindow,
    );
    if (geom != null) {
      this.sendMessage(PostMessageEvent.INLINE_GEOMETRY, geom);
    }
  }

  handleEndPreview() {
    if (!this.iframe) {
      return null;
    }
    this._preview = false;
    this.sendMessage(PostMessageEvent.END_PREVIEW, true);

    this.handlePreviewEnded(this);
  }

  handleCallback(message: CallbackType) {
    if (message === 'viewableImpression' && window.flippxp.vimpTrackerFunc) {
      return window.flippxp.vimpTrackerFunc();
    }
  }

  getHeight(): number {
    return this.iframe?.offsetHeight ?? 0;
  }

  getAdType(): UXType {
    if (this.kevelMetadata?.survey) {
      return UXType.SURVEY;
    } else if (this.kevelMetadata?.ulpUrl) {
      return UXType.ULP;
    } else {
      return UXType.INLINE;
    }
  }

  private handleIframeHeightResize(
    messageContent: Readonly<{ height: number }>,
  ) {
    if (!this.iframe) {
      return null;
    }
    const height = Math.ceil(messageContent.height);
    if (this.iframe.offsetHeight < height) {
      this._expandCount++;
      if (this.expandCount === 1) {
        this.hasScrolledBeyond = false;
      }
    }
    this.iframe.style.height = `${height}px`;

    this.resizeCallback(this, height + 8);
  }

  private handleIframeWidthResize(messageContent: Readonly<{ width: number }>) {
    if (!this.iframe) {
      return null;
    }
    const width = Math.ceil(messageContent.width);
    this.iframe.style.width = `${width}px`;
    this.iframe.style.maxWidth = `${width}px`;
  }

  handleHideIframe() {
    if (!this.iframe) {
      return null;
    }
    this.iframe.style.display = 'none';
  }

  handleExperienceMeasureTest = (message: Readonly<MeasureTest>) => {
    AnalyticsQueue.enqueue(
      createExperienceMeasuredTestBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        options: this.options,
        postalCode: this.geoData?.postal_code,
        testName: message.testName,
        resultName: message.resultName,
        resultLabel: message.resultLabel,
        resultPayload: message.resultPayload,
        testIdentifier: message.testIdentifier,
        country: this.geoData?.country,
      }),
    );
  };

  private readonly handlePostMessages = (event: Readonly<MessageEvent>) => {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return null;
    }
    const message = event.data;
    const messageType = message.type;
    const messageContent = message.data;

    if (!messageType) {
      return;
    }

    if (message.type === PostMessageEvent.ULP) {
      if (message.detail.type === ULPMessageEvent.ITEM_IMPRESSION) {
        this.handleUlpItemImpressions(message.detail);
      } else {
        this.handleUlpPostMessages(message.detail);
      }

      return;
    }

    const requestedMessageType = messageType.replace(REQUEST_PREFIX, '');

    switch (requestedMessageType) {
      case PostMessageEvent.SEND_EV:
        this.handleSendEv(messageContent);
        break;
      case PostMessageEvent.INLINE_GEOMETRY:
        this.handleInlineGeometry();
        break;
      case PostMessageEvent.IFRAME_HEIGHT_RESIZE:
        this.handleIframeHeightResize(messageContent);
        break;
      case PostMessageEvent.IFRAME_WIDTH_RESIZE:
        this.handleIframeWidthResize(messageContent);
        break;
      case PostMessageEvent.END_PREVIEW:
        this.handleEndPreview();
        break;
      case PostMessageEvent.CALLBACK:
        this.handleCallback(messageContent);
        break;
      case PostMessageEvent.MEASURE_TEST:
        this.handleExperienceMeasureTest(messageContent);
        break;
      case PostMessageEvent.HIDE_IFRAME:
        this.handleHideIframe();
        break;
      case PostMessageEvent.PUBLICATION_RENDERED:
        this.handlePublicationRendered(messageContent);
        break;
    }
  };

  private handlePublicationRendered(
    messageContent: Readonly<{ width: number; height: number }>,
  ) {
    if (!isInGoogleAdsIframe()) {
      return;
    }
    resizeGamContainer(
      Math.ceil(messageContent.width),
      Math.ceil(messageContent.height),
    );
  }

  private readonly isFooterPresent = (): boolean => {
    return Boolean(
      this.kevelMetadata?.customContentSection !== undefined &&
        !this.options.startCompact &&
        this.kevelMetadata?.customContentSection == -1 &&
        (this.kevelMetadata.customContentS3 ||
          this.kevelMetadata.customContentHtml),
    );
  };

  private readonly handleUlpPostMessages = (message: Readonly<CustomEvent>) => {
    if (
      message.type === ULPMessageEvent.ERROR &&
      ULPErrors.includes(message.detail)
    ) {
      this.handleUlpErrorMessage(message.detail);
      if (this.contentElement) {
        this.contentElement.style.height = '0';
      }
      return null;
    }

    // eslint-disable-next-line functional/no-let
    let itemType: AnalyticBeacons.ItemType;
    if (!getHasBeenEngaged()) {
      if (this.iframe) {
        this.iframe.dispatchEvent(new Event('flipp-engagement'));
      } else {
        window.dispatchEvent(new Event('flipp-engagement'));
      }
    }

    switch (message.type) {
      case ULPMessageEvent.CLICK_MEATBALL:
      case ULPMessageEvent.SHARE_ITEM:
        itemType = getUlpItemType(message.detail.itemType);
        break;
      case ULPMessageEvent.SEE_FLYER_ITEM:
      case ULPMessageEvent.CLIP_FLYER_ITEM:
        itemType = AnalyticBeacons.ItemType.Flyer;
        break;
      case ULPMessageEvent.SEE_MERCHANT_ITEM:
      case ULPMessageEvent.CLIP_MERCHANT_ITEM:
        itemType = AnalyticBeacons.ItemType.Merchant;
        break;
      case ULPMessageEvent.SEE_ECOM_ITEM:
      case ULPMessageEvent.CLIP_ECOM_ITEM:
        itemType = AnalyticBeacons.ItemType.Ecom;
        break;
      case ULPMessageEvent.SEE_ALL_DEALS:
        this.handleUlpSeeAllDealsClick();
        return;
      default:
        // eslint-disable-next-line functional/no-throw-statements
        throw new Error('ULP message not recognized');
    }
    this.handleUlpClick(itemType, message as ULPMessage);
  };

  private handleUlpSeeAllDealsClick() {
    AnalyticsQueue.enqueue(
      createUlpSeeAllDealsBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        decision: { ...this.data },
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        customUrl: this.getCustomUrl(),
        options: this.options,
        postalCode: this.geoData?.postal_code,
        campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
        positionIndex: this.positionIndex,
      }),
    );
  }

  private handleUlpClick(
    itemType: AnalyticBeacons.ItemType,
    message: Readonly<ULPMessage>,
  ) {
    AnalyticsQueue.enqueue(
      createUlpClickBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        decision: { ...this.data },
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        customUrl: this.getCustomUrl(),
        message,
        itemType,
        options: this.options,
        postalCode: this.geoData?.postal_code,
        campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
        positionIndex: this.positionIndex,
      }),
    );
    return null;
  }

  private handleUlpItemImpressions(message: Readonly<ULPImpressionMessage>) {
    AnalyticsQueue.enqueue(
      createUlpItemImpressionBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        decision: { ...this.data },
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        customUrl: this.getCustomUrl(),
        message,
        options: this.options,
        postalCode: this.geoData?.postal_code,
        campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
        positionIndex: this.positionIndex,
      }),
    );
    return null;
  }

  private handleUlpErrorMessage(errorMessage: string) {
    AnalyticsQueue.enqueue(
      createUlpErrorBeacon({
        errorMessage,
        partnerNameIdentifier: this.publisherNameIdentifier,
        decision: { ...this.data },
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        customUrl: this.getCustomUrl(),
        options: this.options,
        postalCode: this.geoData?.postal_code,
        campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
        positionIndex: this.positionIndex,
      }),
    );

    return null;
  }

  private getCustomUrl() {
    return (
      this.kevelMetadata?.ulpUrl ??
      this.kevelMetadata?.campaignConfigUrl ??
      'no-url'
    );
  }

  private registerScroll() {
    if (!this.containerWindow || this.options.isGoogleAmp) {
      return null;
    }

    this.containerWindow.addEventListener(
      'scroll',
      throttle(
        () => {
          if (
            !this.containerWindow ||
            !this.containerElement ||
            !this.contentElement
          ) {
            return null;
          }

          const geom = calculateGeomData(
            this.containerElement,
            this.contentElement,
            this.containerWindow,
          );
          if (geom !== null) {
            this.sendMessage(PostMessageEvent.INLINE_GEOMETRY, geom);
          }
          return null;
        },
        scrollInterval,
        { leading: false, trailing: true },
      ),
    );
  }

  private registerTags() {
    const campaignManagement = {
      open: [this.impressionUrl],
      engagement: [this.engagementUrl],
      impression: [],
    } as AnalyticsTagEventTypes;
    this.liveRampTag = buildLiveRampTag(this.data, this.geoData);
    if (this.liveRampTag) {
      campaignManagement.impression?.push(this.liveRampTag);
    }
    Tags.addTags(
      { campaignManagement },
      {
        startCompact: this.options?.startCompact || false,
      },
    );
    return null;
  }

  initGoogleAmp(width: number, height: number) {
    window.parent.postMessage(
      { sentinel: 'amp', type: 'send-intersections' },
      '*',
    );
    window.parent.postMessage(
      {
        sentinel: 'amp',
        type: 'embed-size',
        width: width,
        height: height,
      },
      '*',
    );

    window.addEventListener('message', event => {
      if (
        !(
          event.source === window.parent &&
          event.origin !== window.location.origin &&
          event.data &&
          event.data.sentinel === 'amp' &&
          event.data.type === 'intersection'
        )
      ) {
        return null;
      }
      event.data.changes.forEach((change: IntersectionObserverEntry) => {
        // If the google-amp window did not expand sufficently - never activate the impression
        if (change.boundingClientRect.height < MIN_GOOGLE_AMP_HEIGHT) {
          this.sendMessage(PostMessageEvent.INLINE_GEOMETRY, { yiv: 0, h: 0 });
          return null;
        }

        const geom = {
          self: {
            yiv: change.intersectionRatio,
            h: change.boundingClientRect.height,
          },
        };
        this.sendMessage(PostMessageEvent.INLINE_GEOMETRY, geom);
      });
    });
    return null;
  }

  handleNoExperienceServed() {
    // No Experience Served! Anything Served - Fallback is "collapsed"
    // Fallback Image is if you DO get a campaign and there is no FlyerRun found for it.
    // Choice - it's better to collapse than this. Not phase 1, but enhancement.
    //
    // This should no longer be an experience-level issue but a slot level issue.
    // Where we make a request to the parent to resize.
    this.resizeCallback(this, 0);
    return null;
  }

  assignData() {
    this.creativeWidth = this.data.width;
    this.creativeHeight = this.data.height;
    this.engagementUrl = this.data.clickUrl;
    this.impressionUrl = this.data.impressionUrl;
    this._hoverExpandable =
      getPlatform()?.[
        'com.flipp.beacon.shopperPlatform.enumeration.Platform'
      ] === AnalyticBeacons.Platform.Desktop &&
      (this.options.startCompact || this.kevelMetadata?.startCompact)
        ? Boolean(this.kevelMetadata?.hoverExpandable)
        : false;

    return null;
  }

  checkWideCompact(): boolean {
    const deviceInfo = DeviceService.getDeviceInfo();
    return (
      !!this.options.startCompact &&
      !!this.options.wideCompact &&
      !!this.options.wideCompactBackgroundColor &&
      validHexColor(this.options.wideCompactBackgroundColor) &&
      deviceInfo.type === 'desktop'
    );
  }

  calculateTargetDimensions(targetEl: Readonly<HTMLElement>) {
    const isWideCompact = this.checkWideCompact();

    const aspectRatio =
      this.creativeWidth && this.creativeHeight
        ? this.creativeHeight / this.creativeWidth
        : 6;

    const maxWidth =
      this.kevelMetadata?.maxWidth ?? this.options.maxWidth ?? MAX_WIDTH;
    const targetWidth = isWideCompact
      ? Math.max(targetEl.offsetWidth, MIN_WIDTH)
      : Math.max(Math.min(targetEl.offsetWidth, maxWidth), MIN_WIDTH);

    const maxCompactHeight = Math.min(
      window.screen.height - 150,
      COMPACT_HEIGHT,
    );

    const compactHeight =
      this.kevelMetadata?.compactHeight ??
      this.options.compactHeight ??
      maxCompactHeight;
    const standardHeight =
      this.kevelMetadata?.maxHeight ??
      this.kevelMetadata?.standardHeight ??
      this.options.maxHeight ??
      this.options.standardHeight ??
      MAX_HEIGHT;
    const baseHeight = Math.min(
      this.options.preview ? PREVIEW_HEIGHT : targetWidth * aspectRatio,
      this.options.startCompact || this.kevelMetadata?.startCompact
        ? compactHeight
        : standardHeight,
    );
    const targetHeight = isWideCompact ? WIDE_COMPACT_FIXED_HEIGHT : baseHeight;

    return { targetWidth, targetHeight };
  }

  isULPorSurveyPreview() {
    return (
      (this._preview && this.kevelMetadata?.ulpUrl) ||
      (this._preview && this.kevelMetadata?.survey)
    );
  }

  initializeIframe(
    targetWidth: number,
    targetHeight: number,
    ulpUrl: string | undefined,
  ) {
    const iframe = document.createElement('iframe');

    iframe.className = 'flippxp-frame';
    iframe.style.background = 'rgba(255, 255, 255, 0)';
    iframe.style.width = this.options.nestedIframeFullBleed
      ? '100%'
      : `${targetWidth}px`;
    const appConfig = this.composeAppConfig();

    const customContentHeight =
      appConfig?.dynamicModule?.customContentInfo?.customContentHeight;

    iframe.style.height = `${targetHeight}px`;

    if (appConfig?.isStandaloneDynamicModule) {
      iframe.style.height = `${customContentHeight ?? 0}px`;
    }

    const base64AppConfig =
      'flipp-' + btoa(encodeURIComponent(JSON.stringify(appConfig)));
    iframe.setAttribute('scrolling', 'no');
    iframe.style.border = 'none';
    iframe.setAttribute('allow', 'web-share');
    iframe.setAttribute('name', base64AppConfig);
    if (this.options.fixedHeightUnit) {
      iframe.style.borderRadius = '8px';
      iframe.style.border = '1px solid rgba(235, 238, 243, 1)';
    }
    // eslint-disable-next-line functional/no-let
    let appUrl = '__appUrl';
    if (ulpUrl) {
      appUrl = ulpUrl;
      if (this.geoData?.postal_code && !ulpUrl.includes('horizontal-test')) {
        appUrl = `${appUrl}&postal_code=${this.geoData?.postal_code}`;
      }
    } else {
      const isWideCompact = this.checkWideCompact();
      const maxWidth = this.kevelMetadata?.maxWidth ?? this.options.maxWidth;
      if (isWideCompact) {
        iframe.style.maxWidth = '100%';
      } else if (maxWidth) {
        iframe.style.maxWidth = `${maxWidth}px`;
      } else if (this.options.startCompact) {
        iframe.style.maxWidth = '650px';
      } else {
        iframe.style.maxWidth = this.options.standalone ? '1024px' : '800px';
      }
      // center iframe content
      iframe.style.margin = '0 auto';
      iframe.style.display = 'block';
    }

    const url = new URL(appUrl);

    if (this.preview) {
      url.searchParams.append('preview', 'true');
    }

    // Set based on what env of app you want
    iframe.setAttribute('src', url.toString());
    return iframe;
  }

  registerUlpMessageListeners(iframe: Readonly<HTMLIFrameElement>) {
    window.addEventListener('message', event => {
      this.handlePostMessages(event);
      return null;
    });
    iframe.addEventListener('flipp-view', () => {
      AnalyticsQueue.enqueue(
        createUlpViewBeacon({
          partnerNameIdentifier: this.publisherNameIdentifier,
          decision: { ...this.data },
          siteId: this.siteId,
          zoneId: this.zoneIds[0] || 0,
          customUrl: this.getCustomUrl(),
          options: this.options,
          postalCode: this.geoData?.postal_code,
          campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
          positionIndex: this.positionIndex,
        }),
      );
      return null;
    });
    iframe.addEventListener(
      'flipp-open',
      () => {
        AnalyticsQueue.enqueue(
          createUlpOpenBeacon({
            partnerNameIdentifier: this.publisherNameIdentifier,
            decision: { ...this.data },
            siteId: this.siteId,
            zoneId: this.zoneIds[0] || 0,
            customUrl: this.getCustomUrl(),
            options: this.options,
            postalCode: this.geoData?.postal_code,
            campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
            positionIndex: this.positionIndex,
          }),
        );
        Tags.sendOpen();
        if (typeof window.flippxp.vimpTrackerFunc === 'function') {
          window.flippxp.vimpTrackerFunc();
        }
        return null;
      },
      {
        once: true,
      },
    );
    iframe.addEventListener('flipp-heartbeat', evt => {
      const time = evt.detail.time;
      AnalyticsQueue.enqueue(
        createUlpHeartbeatBeacon({
          partnerNameIdentifier: this.publisherNameIdentifier,
          elapsedSeconds: Math.round(time / 1000),
          decision: { ...this.data },
          siteId: this.siteId,
          zoneId: this.zoneIds[0] || 0,
          customUrl: this.getCustomUrl(),
          options: this.options,
          postalCode: this.geoData?.postal_code,
          campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
          positionIndex: this.positionIndex,
        }),
      );
      return null;
    });
    iframe.addEventListener(
      'flipp-engagement',
      () => {
        AnalyticsQueue.enqueue(
          createUlpEngagementBeacon({
            partnerNameIdentifier: this.publisherNameIdentifier,
            decision: { ...this.data },
            siteId: this.siteId,
            zoneId: this.zoneIds[0] || 0,
            customUrl: this.getCustomUrl(),
            options: this.options,
            postalCode: this.geoData?.postal_code,
            campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
            positionIndex: this.positionIndex,
          }),
        );
        Tags.sendEngagement();
        return null;
      },
      {
        once: true,
      },
    );
  }

  render() {
    if (this.data?.contents == null || this.data.contents.length === 0) {
      this.handleNoExperienceServed();
      return null;
    }
    this.kevelMetadata = this.data.contents[0].data
      .customData as KevelCustomData;
    this.assignData();
    this.registerTags();
    // Grab Div
    const el = this.targetEl;

    // Grab optional header
    const header = el.querySelector<HTMLElement>('.flipp-ux-header');
    if (
      header &&
      !this.kevelMetadata.hideHeader &&
      !this.kevelMetadata.dynamicModule?.standalone
    ) {
      header.style.display = 'block';
    }

    const { targetWidth, targetHeight } = this.calculateTargetDimensions(el);

    if (this.options.isGoogleAmp) {
      this.initGoogleAmp(targetWidth, targetHeight);
    }

    // Never render a ULP or Survey in preview
    if (this.isULPorSurveyPreview()) {
      return;
    }

    // eslint-disable-next-line functional/no-let
    let footerHeight = 0;
    if (this.isFooterPresent()) {
      footerHeight = this.kevelMetadata?.customContentHeight ?? 0;
    }

    this.iframe = this.initializeIframe(
      targetWidth,
      targetHeight - footerHeight,
      this.kevelMetadata.ulpUrl,
    );

    this._contentElement = this.iframe;
    el.appendChild(this.iframe);
    watchAdmixerViewable(this.iframe, this.data);

    // Inject custom content to flyer (Kargo content)
    void this.handleFooterInjection()
      .then(() => console.log('Footer injection completed.'))
      .catch(error => console.error('Footer injection failed:', error));

    if (this.options.fixedHeightUnit) {
      createTaboolaFooter({
        container: el,
        validFrom: convertToISOString(this.data.storefront?.valid_from),
        validTo: convertToISOString(this.data.storefront?.valid_to),
      });
    }
    this.containerWindow = window;
    this.containerElement = el;
    // Resize parent if we must
    // adding additional 8px so the nested iframe doesn't overflow with the content below
    // see MAG-84 for more details
    if (this.options.nestedIframe) {
      const headerHeight = header?.offsetHeight ?? 0;
      const height = this._preview
        ? PREVIEW_HEIGHT
        : document.body.offsetHeight + headerHeight + 8;
      this.resizeCallback(this, height);
      this.containerWindow = window.parent;
      this.containerElement = thisIframeInParent() ?? el;
    }

    // Register Event Listeners
    if (!this.intersectionObserver) {
      console.log('Register Event Listeners');
      this.registerScroll();
    }

    this.registerUlpMessageListeners(this.iframe);
    if (this.kevelMetadata.ulpUrl) {
      trackHeartbeat(
        this.iframe,
        this.intersectionObserver,
        this.kevelMetadata.evTimerMs,
      );
    }
  }

  private readonly createCustomContentContainer = async (
    targetEl: Readonly<HTMLElement>,
    kevelMetadata: Readonly<KevelCustomData> | undefined,
    maxWidth: string,
  ): Promise<HTMLIFrameElement> => {
    const customContentContainer: HTMLIFrameElement = createCustomContentIframe(
      kevelMetadata?.customContentHeight ?? 0,
    );
    customContentContainer.setAttribute(
      'style',
      'width: 100%; border: none; display: block;',
    );
    const wrapper = createCustomContentWrapper(customContentContainer);
    wrapper.style.margin = '8px auto';
    wrapper.style.maxWidth = maxWidth;

    targetEl.appendChild(wrapper);

    await injectCustomContent(customContentContainer, kevelMetadata);
    return customContentContainer;
  };

  private readonly handleFooterInjection = async (): Promise<void> => {
    try {
      if (!this.isFooterPresent()) {
        console.log('Footer injection is absent.');
        return;
      }

      const customContentContainer = await this.createCustomContentContainer(
        this.targetEl,
        this.kevelMetadata,
        this.iframe?.style.maxWidth ? this.iframe.style.maxWidth : '',
      );

      const footerClickListener = () => {
        this.sendExperienceMeasuredTest('Click');
        window.open(this.kevelMetadata?.customContentUrl, '_blank', 'noopener');
      };
      const impressionCallback = () => {
        this.sendExperienceMeasuredTest('Impression');
      };
      attachClickEvent(customContentContainer, footerClickListener, false);
      attachObserver(customContentContainer, impressionCallback);
    } catch (error) {
      console.error('Error during custom content preparation:', error);
    }
  };

  private readonly sendExperienceMeasuredTest = (label: string) => {
    AnalyticsQueue.enqueue(
      createExperienceMeasuredTestBeacon({
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        options: this.options,
        postalCode: this.geoData?.postal_code,
        testName: 'KargoTest',
        resultName: 'Ad',
        resultLabel: label,
        resultPayload: getBeaconPayload(
          this.data.adId,
          this.kevelMetadata?.campaignNameIdentifier ?? '',
          this.kevelMetadata?.customContentSize ?? '',
          this.kevelMetadata?.customContentSection ?? -1,
        ),
        testIdentifier: this.data.campaignId,
        country: this.geoData?.country,
      }),
    );
  };

  public scrollBeyond = () => {
    // Bail if we've already fired once for this experience
    if (this.hasScrolledBeyond) {
      return null;
    }

    this.hasScrolledBeyond = true;

    if (!this.data.storefront) {
      return null;
    }

    const storefront: AnalyticBeacons.Storefront = {
      sfmlUUID:
        this.data.storefront.sfml_uuid ??
        this.data.publication?.global_id ??
        '-1',
      version: null,
      contentHash: null,
    };
    const beacon = {
      ...createBaseBeacon({
        schemaId: AnalyticBeacons.ExperienceScrolledBeyondSlotSchemaId,
        partnerNameIdentifier: this.publisherNameIdentifier,
        siteId: this.siteId,
        zoneId: this.zoneIds[0] || 0,
        options: this.options,
        postalCode: this.geoData?.postal_code,
        campaignNameIdentifier: this.kevelMetadata?.campaignNameIdentifier,
        country: this.geoData?.country,
      }),
      storefront,
      merchant: { merchantId: this.data.storefront.merchant_id ?? -1 },
      decision: getDecisionParameters(
        translateDecisionToAvro({
          decision: this.data,
          siteId: this.siteId,
          zoneId: this.zoneIds[0] || 0,
          positionIndex: this.positionIndex,
          customUrl: this.getCustomUrl(),
        }),
      ),
      height: this.getHeight(),
      expansionCount: this.expandCount,
      flyer: {
        ...getFlyerLegacyIds(
          this.data.storefront,
          (this.options.publicationGlobalId ||
            this.data.publication?.global_id) as string,
        ),
        storeId: null,
        premium: true,
      },
    };

    AnalyticsQueue.enqueue<AnalyticBeacons.ExperienceScrolledBeyondSlot>(
      beacon,
    );
  };
}

export default Experience;
